import React from 'react'
import { Form, Formik } from 'formik'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { omit } from 'lodash'
import { DateTime } from 'luxon'
import { Alert, Box, Button, Grid, Typography } from '@mui/material'
import { PatientHistoryType } from 'src/graphql.generated'
import useAddPatientHistoryEntry from 'src/participantHistory/useAddPatientHistoryEntry'
import { useToasts } from 'react-toast-notifications'
import { MonthlySummaryNoteFields } from 'src/participantHistory/MonthlySummaryNoteFields'
import { Loader } from 'src/designSystem'
import { DraftIndicator } from 'src/designSystem/DraftIndicator'
import { checkIfKaiserHealthPlan } from 'src/participant/participantUtils'
import { useGetPatientCredentialingHealthPlanQuery } from '../checkinCall/getPatientCredentialingHealthPlan.generated'
import SubmitButton from '../SubmitButton'
import { clearFormFromLocalStorage } from '../persistFormHelpers'
import { getMonthlySummaryNoteFormSchema, MonthlySummaryNoteFormType } from './MonthlySummaryNoteFormSchema'
import FormPersistor from '../FormPersistor'
import { useLocalStorageWithPrefillValues } from '../useLocalStorageWithPrefillValues'

const FORM_TYPE = PatientHistoryType.MonthlySummaryNote

export const getDefaultValues = (): MonthlySummaryNoteFormType => ({
  date: DateTime.now().toFormat('yyyy-MM-dd'),
  participant: {
    firstName: '',
    lastName: '',
    memberID: '',
  },
  assessingPractitioner: {
    name: '',
    licenseType: '',
  },
  cptCode: '',
  datesOfService: {
    startDate: '',
    endDate: '',
  },
  treatmentPlan: {
    smartGoals: '',
  },
  billingCodes: [],
  interventions: {
    PHQ9_GAD7: false,
    BIOFEEDBACK: false,
    IN_APP_CHAT: false,
    CBT_MINDFULLNESS_ACT: false,
    PSYCHOEDUCATION: false,
    TELEHEALTH_VIDEO: false,
    PEER_SUPPORT: false,
  },
  reasonForSeekingTreatment: {
    functionalImpairments: {
      DIFFICULTY_MEETING_EXPECTATIONS: false,
      STRUGGLE_WITH_RELATIONSHIPS: false,
      NEGLECT_OF_PERSONAL_NEEDS: false,
      DIFFICULTY_IN_SLEEP: false,
      CHANGE_IN_APPETITE_OR_WEIGHT: false,
      IMPAIRED_COGNITIVE_FUNCTIONING: false,
      VOLATILE_MOOD_RESPONSES: false,
      DIFFICULTY_MANAGING_TASKS: false,
      LOSS_OF_INTEREST: false,
      OTHER: '',
    },
  },
  participantEngagement: {
    participation: '',
    chatSummary: '',
  },
  progressAssessment: {
    sinceLastAssessment: {
      healthQuestionnaire: {
        PHQ9: {
          QUESTION1: 0,
          QUESTION2: 0,
          QUESTION3: 0,
          QUESTION4: 0,
          QUESTION5: 0,
          QUESTION6: 0,
          QUESTION7: 0,
          QUESTION8: 0,
          QUESTION9: 0,
          TOTAL_SCORE: 0,
        },
        GAD7: {
          QUESTION1: 0,
          QUESTION2: 0,
          QUESTION3: 0,
          QUESTION4: 0,
          QUESTION5: 0,
          QUESTION6: 0,
          QUESTION7: 0,
          TOTAL_SCORE: 0,
        },
      },
      assessment: {
        NO_NOTABLE_CHANGE: false,
        OVERALL_IMPROVEMENT: false,
        OVERALL_WORSENING: false,
        FLUCTUATING_PATTERN: false,
        NOT_COMPLETED_QUESTIONNAIRES: false,
        REMINDERS_SENT: false,
        details: '',
      },
    },
    riskAssessment: {
      status: 'noChanges',
      details: '',
    },
    participantResponse: {
      response: 'activelyEngaged',
      details: '',
    },
    plan: {
      planType: 'continueSupport',
      details: '',
    },
  },
})

const MonthlySummaryNoteForm = ({
  uid,
  afterSubmittedCallback,
}: {
  uid: string
  afterSubmittedCallback?: () => void
}) => {
  const addPatientHistoryEntry = useAddPatientHistoryEntry()
  const { addToast } = useToasts()
  const {
    data: patientData,
    loading: isLoadingHealthPlan,
    error: healthPlanError,
    refetch: refetchHealthPlan,
  } = useGetPatientCredentialingHealthPlanQuery({
    variables: { uid },
  })
  const isKaiser = Boolean(checkIfKaiserHealthPlan(patientData?.patient?.credentialingHealthPlan))

  const { values: initialValues, status } = useLocalStorageWithPrefillValues(uid, FORM_TYPE, getDefaultValues)

  if (status === 'loading' || isLoadingHealthPlan) {
    return <Loader size="large" />
  }

  if (healthPlanError) {
    return (
      <Box mt={3}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetchHealthPlan()}>
              Retry
            </Button>
          }
        >
          Failed to load health plan. Retry before submitting this note.
        </Alert>
      </Box>
    )
  }

  return (
    <Grid item>
      <Formik<MonthlySummaryNoteFormType>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={toFormikValidationSchema(getMonthlySummaryNoteFormSchema(isKaiser))}
        onSubmit={async (values, { resetForm }) => {
          try {
            await addPatientHistoryEntry({
              uid,
              date: values.date,
              type: PatientHistoryType.MonthlySummaryNote,
              value: isKaiser ? values : omit(values, 'cptCode'),
            })
            resetForm({ values: initialValues })
            clearFormFromLocalStorage(uid, FORM_TYPE)
            afterSubmittedCallback?.()
          } catch (e) {
            addToast('Failed to submit monthly summary note', {
              appearance: 'error',
            })
          }
        }}
      >
        {({ isSubmitting, errors, dirty }) => (
          <Form>
            {(status === 'draft' || dirty) && <DraftIndicator dirty={dirty} />}
            <MonthlySummaryNoteFields isKaiser={isKaiser} />

            <Box justifyContent="flex-end" display="flex" mt={4}>
              <SubmitButton isSubmitting={isSubmitting} />
            </Box>

            {Object.values(errors).filter((error) => error).length > 0 && (
              <Box mt={3}>
                <Typography variant="body1" color="error">
                  Fill in required fields.
                </Typography>
              </Box>
            )}
            <FormPersistor uid={uid} formType={FORM_TYPE} />
          </Form>
        )}
      </Formik>
    </Grid>
  )
}

export default MonthlySummaryNoteForm
